import { isUndefined } from 'lodash'
import { ApiResult } from 'src/Libs/api'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import {
  CostSavingChartData,
  CostSavingChartDataWithDynamicEms,
  DynamicEmsData,
  EmsBatteryMode,
  NullablePowerPrice,
  PowerPrice,
  ThreeSixtySavings,
  Timestamp,
} from '../../types'
import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { getMatchingTimestampItemFromArray } from 'src/Libs/system-data-handler/utils/chartFunctions'
import moment from '@e3dc-react/shell/Libs/moment'
import { date } from '@e3dc-react/shell/Libs/date'
import { Moment } from 'moment'

/**
 * Adjusts the energy value based on the view and calculates the related spending
 *
 * @param valueInWatt
 * @param price
 * @param view
 * @returns
 */
export const calculateEnergyAndRelatedSpending = (valueInWatt: number, price: number): { energyValue: number; spending: number } => {
  const valueInKiloWattHour = Number((valueInWatt / 1000).toFixed(2))
  const priceInEuro = price / 100
  const spending = Number((priceInEuro * valueInKiloWattHour).toFixed(2))

  return { energyValue: valueInKiloWattHour, spending }
}

/**
 * Transform the 360 energy values to right format (unit, decimal points)
 */
export const transformThreeSixtyEnergyAndSpending = (
  originalEnergy?: number | null,
  originalSaving?: number | null
): { energy: number | null; saving: number | null } => {
  let energyValueInKiloWatt = isUndefined(originalEnergy) ? null : originalEnergy
  let saving = isUndefined(originalSaving) ? null : originalSaving

  if (originalEnergy) energyValueInKiloWatt = Number((originalEnergy / 1000).toFixed(2))
  if (originalSaving) saving = Math.abs(originalSaving) < 0.01 ? 0 : Number(originalSaving.toFixed(2))

  //todo: those can never be true, as abs is only ever 0 for inputs (0, -0), which are both falsy!?
  if (energyValueInKiloWatt && Math.abs(energyValueInKiloWatt) === 0) energyValueInKiloWatt = 0
  if (saving && Math.abs(saving) === 0) saving = 0

  return { energy: energyValueInKiloWatt, saving: saving }
}

/**
 * Returns localized energy and saving values with two decimal points
 *
 * @param energy
 * @param saving
 * @returns
 */
export const getLocalizedCostSavingChartValues = (energy: number, savings?: number): { localizedEnergy: string; localizedSavings: string } => {
  const decimalPoints = 2
  const localizedEnergy = getLocalizedNumber(energy, decimalPoints)
  const localizedSavings = !isUndefined(savings) ? getLocalizedNumber(savings, decimalPoints) : '-'

  return { localizedEnergy, localizedSavings }
}

/**
 * Based on the selected time frame a fixed number of time-ticks are required (e.g. 24 ticks for a day). The backend only sends data for ticks if any exist.
 * This function fills the missing time-ticks with zero-values.
 */
export const fillDataWithEmptyTimestampValues = (
  data: ApiResult<ThreeSixtySavings[]> | ThreeSixtySavings[] | null,
  view: ChartView,
  filledTimeValuesForCurrentView?: Timestamp[]
): ThreeSixtySavings[] | undefined => {
  if (filledTimeValuesForCurrentView) {
    const today = new Date()
    const dataToReturn: ThreeSixtySavings[] = []
    const values = Array.isArray(data) ? data : data?.value

    for (const { ts } of filledTimeValuesForCurrentView) {
      const isAfterToday = ts > today.getTime()
      const fallbackData = isAfterToday ? { time: moment(ts), netOutWh: null, savings: null } : { time: moment(ts), netOutWh: 0, savings: 0 }
      const foundValue = getMatchingTimestampItemFromArray(values || [], ts, view)
      dataToReturn.push(foundValue ?? fallbackData)
    }

    return dataToReturn
  }
}

/*
 * Maps cost saving chart data with dynamic ems data to display the dynamic ems battery modes in the chart
 */
export const getCostSavingChartDataWithDynamicEms = (
  data: CostSavingChartData[],
  dynamicEmsData: DynamicEmsData[]
): CostSavingChartDataWithDynamicEms[] => {
  return data
    .map(item => {
      const chartDataItemTime = new Date(item.time)
      const matchingEmsDataEntry = dynamicEmsData.find(emsDataEntry => {
        const startTime = new Date(emsDataEntry.start)
        const endTime = new Date(emsDataEntry.end)

        return (date.isEqual(chartDataItemTime, startTime) || date.isAfter(chartDataItemTime, startTime)) && date.isBefore(chartDataItemTime, endTime)
      })
      const batteryMode = matchingEmsDataEntry?.emsBatteryMode || EmsBatteryMode.NormalOperation

      return { ...item, batteryMode, [batteryMode]: item.consumptionPrice || 0 }
    })
    .map((item, index, originalArray) => {
      const { batteryMode: currentBatteryMode, consumptionPrice } = item
      const previousBatteryMode = originalArray[index - 1]?.batteryMode
      const price = consumptionPrice || 0
      const nextPrice = originalArray[index + 1]?.consumptionPrice

      /* This condition is needed to ensure the dynamic ems areas are not shown beyond the price line (overflow). This is not relevant for the edges (first and last entry) */
      if (index !== 0 && index !== originalArray.length - 1 && price !== null && nextPrice === null) {
        return { ...item, [currentBatteryMode]: 0 }
      }

      /* This condiotion is needed to avoid having white gaps in the dynamic ems areas */
      if (index !== 0 && index !== originalArray.length - 1 && previousBatteryMode !== currentBatteryMode) {
        return { ...item, [currentBatteryMode]: price, [previousBatteryMode]: 0 }
      }

      return { ...item, [currentBatteryMode]: price }
    })
}

/**
 * Filter out prices that are not in the requested time range.
 *
 * @param prices
 * @param from
 * @param to
 * @returns
 */
export const filterOutInvalidTimestamps = (
  prices: PowerPrice[] | NullablePowerPrice[],
  from?: Moment,
  to?: Moment
): PowerPrice[] | NullablePowerPrice[] => {
  const filteredPrices: (PowerPrice | NullablePowerPrice)[] = []
  const normalizedFrom = from?.clone().milliseconds(0)
  const normalizedTo = to?.clone().milliseconds(0)

  for (const price of prices) {
    const isPriceValidityBeforeValidRange = moment(price.validTo).isSameOrBefore(normalizedFrom)
    const isPriceValidityAfterValidRange = moment(price.validFrom).isSameOrAfter(normalizedTo)

    if (isPriceValidityBeforeValidRange || isPriceValidityAfterValidRange) continue

    const isInvalidFromButValidTo = moment(price.validFrom).isBefore(normalizedFrom) && moment(price.validTo).isAfter(normalizedFrom)
    const isInvalidToButValidFrom = moment(price.validTo).isAfter(normalizedTo) && moment(price.validFrom).isBefore(normalizedTo)

    if (normalizedFrom && isInvalidFromButValidTo) {
      filteredPrices.push({
        ...price,
        validFrom: normalizedFrom.toISOString(),
      })
      continue
    }

    if (normalizedTo && isInvalidToButValidFrom) {
      filteredPrices.push({
        ...price,
        validTo: normalizedTo.toISOString(),
      })
      continue
    }

    filteredPrices.push(price)
  }

  return filteredPrices
}

/**
 * Filter out prices that are not in the requested time range.
 * @param prices PowerPrices-Array (eg. from the fetch for the widget or modal)
 * @param from date range start
 * @param to date range end
 * @returns Filtered PowerPrices-Array
 */
export const filterOutOfRangePrices = (prices: PowerPrice[] | undefined, from: moment.Moment, to: moment.Moment): PowerPrice[] | undefined => {
  return prices?.filter(price => {
    const validFrom = moment(price.validFrom)
    const validTo = moment(price.validTo)
    return validFrom.isBefore(to) && validTo.isAfter(from)
  })
}
